/**
 * serpentSkeleton — a procedural serpent/snake-skeleton silhouette generator.
 * Produces normalized geometry (0..1 box) from a seed: a sinuous spine curve
 * (S-shape), rib pairs along the body, a wedge-shaped skull, and a tapering
 * tail. The caller scales to the target width.
 *
 * Deterministic: all randomness via `seededRand` (NO Math.random). Same seed ⇒
 * identical geometry — goldens stable across reloads.
 *
 * Render-agnostic: returns points, not Pixi objects.
 */
import { seededRand } from '../atmospherePlacement';
import type { Point } from './types';

/** Configurable structural constants (BUILD_STANDARDS §1 — no magic numbers inline). */
const RIB_PAIR_COUNT_MIN = 14;
const RIB_PAIR_COUNT_MAX = 22;
const RIB_LENGTH_MID = 0.14;     // fraction of bbox height — longest rib (mid-body)
const RIB_LENGTH_TAIL = 0.02;    // shortest rib (tail end)
const SPINE_AMPLITUDE = 0.18;    // S-curve amplitude (fraction of height)
const SPINE_WAVES = 2.5;         // number of S-curves along the body
const SKULL_LENGTH = 0.08;       // skull as fraction of total length
const SKULL_HEIGHT = 0.16;       // skull height as fraction of bbox height
const TAIL_TAPER_START = 0.85;   // where the body starts tapering to a point

/**
 * Generate a serpent-skeleton silhouette in a normalized 0..1 box.
 * Width = 1.0 (head at x=0, tail at x=1); height derived from the aspect ratio.
 */
export function serpentSkeleton(seed: number, detail = 1): {
  outline: Point[];
  parts: Array<{ points: Point[] }>;
  bbox: { w: number; h: number };
} {
  const ribPairs = Math.round(
    RIB_PAIR_COUNT_MIN + seededRand(seed) * (RIB_PAIR_COUNT_MAX - RIB_PAIR_COUNT_MIN),
  ) * Math.max(0.5, Math.min(1.5, detail));

  // Spine: a sinuous S-curve from head to tail.
  const spinePoints: Point[] = [];
  const spineSegments = 32;
  for (let i = 0; i <= spineSegments; i += 1) {
    const t = i / spineSegments;
    const y = 0.5 + Math.sin(t * Math.PI * 2 * SPINE_WAVES) * SPINE_AMPLITUDE;
    spinePoints.push({ x: t, y });
  }

  // Helper: spine Y at a given x.
  const spineY = (x: number): number =>
    0.5 + Math.sin(x * Math.PI * 2 * SPINE_WAVES) * SPINE_AMPLITUDE;

  const parts: Array<{ points: Point[] }> = [];

  // Skull: a wedge-shaped head at x ≈ 0..SKULL_LENGTH.
  const skullCy = spineY(0);
  const skullW = SKULL_LENGTH;
  const skullH = SKULL_HEIGHT;
  const skullOutline: Point[] = [
    { x: 0, y: skullCy },
    { x: skullW * 0.3, y: skullCy - skullH * 0.5 },
    { x: skullW, y: skullCy - skullH * 0.3 },
    { x: skullW, y: skullCy + skullH * 0.3 },
    { x: skullW * 0.3, y: skullCy + skullH * 0.5 },
  ];
  parts.push({ points: skullOutline });

  // Rib pairs along the body (skip skull + tail taper zone).
  const ribStart = SKULL_LENGTH;
  const ribEnd = TAIL_TAPER_START;
  const ribStep = (ribEnd - ribStart) / ribPairs;
  for (let i = 0; i < ribPairs; i += 1) {
    const t = i / Math.max(1, ribPairs - 1);
    const x = ribStart + i * ribStep;
    const sy = spineY(x);
    // Rib length tapers: longest at mid-body, shortest at the tail.
    const lengthFrac = RIB_LENGTH_MID * (1 - t * 0.8) + RIB_LENGTH_TAIL * t;
    const variation = 0.85 + seededRand(seed + i * 4.1) * 0.3;
    const halfLen = lengthFrac * variation * 0.5;
    // Rib angle: roughly perpendicular to the spine tangent.
    const tangentAngle = Math.cos(x * Math.PI * 2 * SPINE_WAVES) * 0.3;
    // Upper rib.
    const ribPoints: Point[] = [];
    const ribSegments = 3;
    for (let j = 0; j <= ribSegments; j += 1) {
      const r = j / ribSegments;
      const y = sy - halfLen * Math.sin(r * Math.PI * 0.5);
      const dx = r * tangentAngle * 0.01;
      ribPoints.push({ x: x + dx, y });
    }
    parts.push({ points: ribPoints });
    // Lower rib.
    const ribPoints2: Point[] = [];
    for (let j = 0; j <= ribSegments; j += 1) {
      const r = j / ribSegments;
      const y = sy + halfLen * Math.sin(r * Math.PI * 0.5);
      const dx = r * tangentAngle * 0.01;
      ribPoints2.push({ x: x + dx, y });
    }
    parts.push({ points: ribPoints2 });
  }

  // Tail: a tapering line from the last rib to the tip.
  const tailX = TAIL_TAPER_START;
  const tailY = spineY(tailX);
  parts.push({
    points: [
      { x: tailX, y: tailY },
      { x: 1.0, y: spineY(1.0) },
    ],
  });

  // Body outline: follows the spine curve with rib-bulge thickness.
  const outline: Point[] = [];
  const outlineSegments = 28;
  // Top edge: head → over the ribcage → taper to tail tip.
  for (let i = 0; i <= outlineSegments; i += 1) {
    const t = i / outlineSegments;
    if (t < SKULL_LENGTH) {
      const st = t / SKULL_LENGTH;
      outline.push({ x: t, y: skullCy - skullH * 0.5 * (1 - st * 0.5) });
    } else if (t > TAIL_TAPER_START) {
      // Taper to a point at the tail.
      const tt = (t - TAIL_TAPER_START) / (1 - TAIL_TAPER_START);
      const sy = spineY(t);
      const thickness = RIB_LENGTH_MID * 0.3 * (1 - tt);
      outline.push({ x: t, y: sy - thickness });
    } else {
      const rt = (t - SKULL_LENGTH) / (TAIL_TAPER_START - SKULL_LENGTH);
      const heightFrac = RIB_LENGTH_MID * (1 - rt * 0.5) + RIB_LENGTH_TAIL * rt;
      const sy = spineY(t);
      outline.push({ x: t, y: sy - heightFrac * 0.5 });
    }
  }
  // Bottom edge: tail tip → under the ribcage → head bottom (reverse).
  for (let i = outlineSegments; i >= 0; i -= 1) {
    const t = i / outlineSegments;
    if (t < SKULL_LENGTH) {
      const st = t / SKULL_LENGTH;
      outline.push({ x: t, y: skullCy + skullH * 0.5 * (1 - st * 0.5) });
    } else if (t > TAIL_TAPER_START) {
      const tt = (t - TAIL_TAPER_START) / (1 - TAIL_TAPER_START);
      const sy = spineY(t);
      const thickness = RIB_LENGTH_MID * 0.3 * (1 - tt);
      outline.push({ x: t, y: sy + thickness });
    } else {
      const rt = (t - SKULL_LENGTH) / (TAIL_TAPER_START - SKULL_LENGTH);
      const heightFrac = RIB_LENGTH_MID * (1 - rt * 0.5) + RIB_LENGTH_TAIL * rt;
      const sy = spineY(t);
      outline.push({ x: t, y: sy + heightFrac * 0.5 });
    }
  }

  return {
    outline,
    parts,
    bbox: { w: 1, h: SPINE_AMPLITUDE * 2 + RIB_LENGTH_MID + 0.1 },
  };
}
